import { useEffect, useMemo, useState } from "react";
import type { Board, ScheduledRow } from "../api.js";
import type { Priority } from "../../domain/types.js";
import { backlogRows, inTeamBacklog, projectsWithWork, toggleType } from "../backlogFilter.js";

const WORK_TYPES = ["Submittal", "Detail", "Resubmittal", "Fabrication", "Engineering", "Other"] as const;

export function Kpis({ board }: { board: Board }) {
  const k = board.kpis;
  return (
    <div className="kpis">
      <div className="kpi">
        <span className="lab">Remaining work</span>
        <span className="val">{k.remainingHours.toLocaleString()}<span style={{ fontSize: 14 }}> h</span></span>
        <span className="sub">was {k.workbookHours.toLocaleString()} under ABS()</span>
      </div>
      <div className="kpi">
        <span className="lab">Open phases</span>
        <span className="val">{k.openPhases}</span>
        <span className="sub">across {k.projects} projects</span>
      </div>
      <div className="kpi">
        <span className="lab">Late</span>
        <span className={`val${k.late ? " crit" : ""}`}>{k.late}</span>
        <span className="sub">past required date</span>
      </div>
      <div className="kpi">
        <span className="lab">Unassigned</span>
        <span className={`val${k.unassigned ? " crit" : ""}`}>{k.unassigned}</span>
        <span className="sub">{k.unassignedHours} h in the pool</span>
      </div>
      <div className="kpi">
        <span className="lab">Overstated by</span>
        <span className="val good">{k.overstatedBy}<span style={{ fontSize: 14 }}> h</span></span>
        <span className="sub">the ABS() defect, not reproduced</span>
      </div>
    </div>
  );
}

export function Dashboard({ board, go, fixOverEstimate }: { board: Board; go: (v: string) => void; fixOverEstimate?: () => void }) {
  const k = board.kpis;
  const noStatus = board.phases.filter((p) => !p.epicorEngStatus).length;
  const overEstimate = board.phases.filter((p) => p.overEstimate).length;
  const overCapacity = useMemo(() => {
    const names = new Map(board.resources.map((r) => [r.resourceId, r.name]));
    return board.pressure
      .filter((p) => p.pct > 1)
      .map((p) => ({ name: names.get(p.resourceId) ?? p.resourceId, pct: p.pct }));
  }, [board]);

  const alerts = [
    k.unassigned > 0 && {
      n: k.unassigned,
      text: `phases have no owner — ${k.unassignedHours} h sitting in the pool`,
      act: "Assign",
      to: "resource",
      crit: true,
    },
    overCapacity.length > 0 && {
      n: overCapacity.length,
      text: `promised more than fits in the next 4 weeks — ${overCapacity.slice(0, 2).map((o) => `${o.name} at ${Math.round(o.pct * 100)}%`).join(", ")}`,
      act: "Rebalance",
      to: "resource",
      crit: true,
    },
    overEstimate > 0 && {
      n: overEstimate,
      text: "open steps are at or over their estimate — complete them, or re-estimate what is left",
      act: "Fix",
      to: "backlog",
      crit: false,
      onGo: fixOverEstimate,
    },
    k.late > 0 && { n: k.late, text: "phases past their required date", act: "Review", to: "backlog", crit: false },
    noStatus > 0 && {
      n: noStatus,
      text: "phases carry no workflow status — metrics cover only the rest",
      act: "Details",
      to: "backlog",
      crit: false,
    },
  ].filter(Boolean) as { n: number; text: string; act: string; to: string; crit: boolean; onGo?: () => void }[];

  return (
    <div>
      <h2 className="sec">Today · needs a decision</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
        {alerts.map((a) => (
          <div
            key={a.text}
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 12,
              padding: "9px 13px",
              borderLeft: `3px solid ${a.crit ? "var(--crit)" : "var(--amber)"}`,
              background: a.crit ? "rgba(199,28,34,0.10)" : "rgba(232,101,34,0.10)",
            }}
          >
            <b style={{ fontFamily: "var(--pg-font-mono)", fontSize: 15 }}>{a.n}</b>
            <span>{a.text}</span>
            <span className="spacer" />
            <button className="btn" onClick={() => (a.onGo ? a.onGo() : go(a.to))}>{a.act} →</button>
          </div>
        ))}
        {alerts.length === 0 && <p className="muted">Nothing needs a decision right now.</p>}
      </div>
      <h2 className="sec">This week</h2>
      <Kpis board={board} />
    </div>
  );
}

interface BacklogProps {
  board: Board;
  /** Show only open steps at or over their estimate. Held by the caller so the Dashboard can switch it on. */
  overOnly?: boolean;
  onOverOnly?: (on: boolean) => void;
  /** Double-clicking a row opens its project, where the step is edited. */
  onOpenProject?: (projectId: string) => void;
  onMove: (
    id: string, patch: { resourceId?: string | null; queuePosition?: number; priority?: Priority },
  ) => Promise<{ ok: boolean; error?: string }>;
}

export function Backlog({ board, onMove, overOnly = false, onOverOnly, onOpenProject }: BacklogProps) {
  // Which work types are showing. Empty is every type, and is how this always opens;
  // Ctrl/Cmd-click a type to add it to the others.
  const [types, setTypes] = useState<ReadonlySet<string>>(new Set());
  // One person's steps, across every work type, like a project.
  const [personId, setPersonId] = useState("");
  // One project's steps across every work type. Choosing one shows them all together.
  const [projectId, setProjectId] = useState("");
  // One team's backlog: the steps its people hold, and the unassigned ones of its project types.
  const [teamId, setTeamId] = useState("");
  const [drag, setDrag] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  // Keyed by assignmentId, so a refusal shows on the row it happened to
  // rather than only in a toast that clears itself after a few seconds —
  // easy to miss while looking at the list, not the corner of the screen.
  const [rowErr, setRowErr] = useState<Record<string, string>>({});
  const phases = useMemo(() => new Map(board.phases.map((p) => [p.phaseId, p])), [board.phases]);
  const projects = useMemo(() => new Map(board.projects.map((p) => [p.projectId, p])), [board.projects]);
  const resources = useMemo(() => new Map(board.resources.map((r) => [r.resourceId, r])), [board.resources]);

  const currentScenario = board.scenarios.find((s) => s.scenarioId === board.scenarioId);
  const isLive = currentScenario?.status === "published";

  function assign(assignmentId: string, resourceId: string | null) {
    void onMove(assignmentId, { resourceId }).then((r) => {
      setRowErr((m) => {
        if (r.ok) {
          if (!(assignmentId in m)) return m;
          const { [assignmentId]: _dropped, ...rest } = m;
          return rest;
        }
        return { ...m, [assignmentId]: r.error ?? "Could not save that." };
      });
    });
  }

  const teamOf = new Map(board.teams.map((t) => [t.teamId, t]));
  const inTeam = (a: { phaseId: string; resourceId: string | null }) =>
    inTeamBacklog(
      teamId, teamOf.get(teamId), a.resourceId ? resources.get(a.resourceId) : undefined,
      projects.get(phases.get(a.phaseId)?.projectId ?? "")?.productCode,
    );
  const rows = backlogRows(
    board.assignments
      .map((a) => ({ a, p: phases.get(a.phaseId)! }))
      .filter((r) => r.p)
      .map((r) => ({ ...r, assignment: r.a, phase: r.p })),
    types, projectId, overOnly, (r) => inTeam(r.a) && (!personId || r.a.resourceId === personId),
  );
  // Arriving with the filter on (the Dashboard's Fix button) lands on the whole list, not one work type.
  useEffect(() => { if (overOnly) setTypes(new Set()); }, [overOnly]);
  const personChoices = useMemo(
    () => board.resources.filter((r) => r.active && !r.isPool).sort((a, b) => a.name.localeCompare(b.name)),
    [board.resources],
  );
  const projectChoices = useMemo(() => projectsWithWork(board.projects, board.phases), [board.projects, board.phases]);
  const pickProject = (id: string) => {
    setProjectId(id);
    // A project is looked at as a whole, so picking one shows every work type.
    if (id) setTypes(new Set());
  };
  const pickPerson = (id: string) => {
    setPersonId(id);
    // Likewise a person: everything they hold, whatever its type.
    if (id) setTypes(new Set());
  };
  const inProject = (a: { phaseId: string; resourceId: string | null }) =>
    (!projectId || phases.get(a.phaseId)?.projectId === projectId) && inTeam(a)
    && (!personId || a.resourceId === personId);
  // A step at or past its estimate but not finished still belongs in the list, flagged.
  const listed = (a: { phaseId: string }) => {
    const p = phases.get(a.phaseId);
    return Boolean(p && (overOnly ? p.overEstimate : (p.remainingHours > 0 || p.overEstimate)));
  };

  const onDrop = (target: ScheduledRow) => () => {
    if (!drag || drag === target.assignmentId) return;
    // Take the target row's slot; the server renumbers the rest of the queue.
    void onMove(drag, { queuePosition: target.queuePosition ?? 1 });
    setDrag(null);
    setOverId(null);
  };

  const tabCount = rows.length;
  const tabHours = rows.reduce((n, r) => n + r.p.remainingHours, 0);

  return (
    <div>
      {isLive ? (
        <div className="err" style={{ marginBottom: 10 }}>
          <b>You're editing the live schedule.</b> Reassigning someone here will be rejected —
          create a scenario (above) to move work, then publish it when it's right. Reordering
          and priority still work directly.
        </div>
      ) : (
        <div className="chip draft" style={{ marginBottom: 10, width: "fit-content" }}>
          Editing “{currentScenario?.name ?? "a draft scenario"}” — not live yet
        </div>
      )}
      <p className="hint" style={{ marginTop: 0, marginBottom: 10 }}>
        <b>This is the running order.</b> One row per step with work left, in the order the
        person assigned to it will actually do it — <b>row 1 is next</b> — grouped by work
        type. It is where you change what gets done first, as opposed to the Schedule, which
        shows you when the current order lands.
        <br />
        Drag ⠿ to reorder, and every later date for that person recalculates. <b>Est</b> is
        what was quoted; <b>Remaining</b> is what is actually left, so a step already over
        its estimate shows 0 and is flagged <b>over estimate</b> until someone completes it or re-estimates it. Changing the assignee moves the step to the end
        of the new person's queue.
      </p>
      <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center", flexWrap: "wrap" }}>
        <label className="fld-inline" style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 13 }}>
          project
          <select id="backlog-project" value={projectId} onChange={(e) => pickProject(e.target.value)}>
            <option value="">all</option>
            {projectChoices.map((p) => (
              <option key={p.projectId} value={p.projectId}>
                {p.projectId}{p.description ? ` — ${p.description.slice(0, 40)}` : ""}
              </option>
            ))}
          </select>
        </label>
        {projectId && <button className="btn" onClick={() => pickProject("")}>Clear</button>}
        <label className="fld-inline" style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 13 }}>
          person
          <select id="backlog-person" value={personId} onChange={(e) => pickPerson(e.target.value)}>
            <option value="">all</option>
            {personChoices.map((r) => <option key={r.resourceId} value={r.resourceId}>{r.name}</option>)}
          </select>
        </label>
        {personId && <button className="btn" onClick={() => pickPerson("")}>Clear person</button>}
        <label className="fld-inline" style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 13 }}>
          team
          <select id="backlog-team" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
            <option value="">all</option>
            {board.teams.map((t) => <option key={t.teamId} value={t.teamId}>{t.name}</option>)}
          </select>
        </label>
        {teamId && <button className="btn" onClick={() => setTeamId("")}>Clear team</button>}
        {onOverOnly && (
          <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 13, marginLeft: 8 }}
            title="Open steps whose logged hours have reached their estimate. Complete them, or give them a new estimate.">
            <input id="backlog-over-only" type="checkbox" checked={overOnly}
              onChange={(e) => { onOverOnly(e.target.checked); if (e.target.checked) setTypes(new Set()); }} />
            Over estimate only
          </label>
        )}
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center", flexWrap: "wrap" }}>
        <button className={`btn${types.size === 0 ? " on" : ""}`} onClick={() => setTypes(new Set())}>
          All types {board.assignments.filter((a) => inProject(a) && listed(a)).length}
        </button>
        {WORK_TYPES.map((t) => {
          const n = board.assignments.filter(
            (a) => inProject(a) && phases.get(a.phaseId)?.workType === t && listed(a),
          ).length;
          return (
            <button key={t} className={`btn${types.has(t) ? " on" : ""}`}
              title="Click for this type alone. Ctrl-click (Cmd-click on a Mac) to add or remove a type."
              onClick={(e) => setTypes(toggleType(types, t, e.ctrlKey || e.metaKey))}>
              {t} {n}
            </button>
          );
        })}
      </div>
      <div className="hint" style={{ margin: "0 0 10px" }}>
        {tabCount} {types.size === 0 ? "" : `${[...types].map((t) => t.toLowerCase()).join(" + ")} `}phase{tabCount === 1 ? "" : "s"} {overOnly ? "over estimate and not complete" : "with work left"}{projectId ? ` on ${projectId}` : ""}{personId ? ` for ${resources.get(personId)?.name ?? "this person"}` : ""}{teamId ? ` for ${teamOf.get(teamId)?.name ?? "this team"}` : ""} ·{" "}
        {Math.round(tabHours)} h remaining
      </div>
      <div className="scroll tall">
        <table className="qt">
          <thead>
            <tr>
              <th></th><th>#</th><th>Priority</th><th>Project</th><th>Customer</th><th>Phase</th>
              <th>Disc</th><th className="r">Est</th><th className="r">Remaining</th><th>Due</th>
              <th>Status</th><th>Assignee</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ a, p }, i) => {
              const project = projects.get(p.projectId);
              const late = p.reqDueDate ? p.reqDueDate < board.today : false;
              return (
                <tr
                  key={a.assignmentId}
                  className={`${!a.resourceId ? "unassigned" : ""}${overId === a.assignmentId ? " drop-target" : ""}`}
                  onDragOver={(e) => { e.preventDefault(); setOverId(a.assignmentId); }}
                  onDragLeave={() => setOverId(null)}
                  onDrop={onDrop(a)}
                  // Not from the dropdowns: double-clicking one to change it is not asking to leave.
                  onDoubleClick={onOpenProject
                    ? (e) => { if (!(e.target as HTMLElement).closest("select, input, button, option")) onOpenProject(p.projectId); }
                    : undefined}
                  title={onOpenProject ? "Double-click to open the project and edit this step" : undefined}
                >
                  <td
                    className="grip"
                    draggable
                    onDragStart={() => setDrag(a.assignmentId)}
                    onDragEnd={() => { setDrag(null); setOverId(null); }}
                  >
                    ⠿
                  </td>
                  <td className="mono">{i + 1}</td>
                  <td>
                    <select
                      value={a.priority}
                      onChange={(e) => void onMove(a.assignmentId, { priority: e.target.value as Priority })}
                    >
                      <option>Low</option><option>Standard</option><option>Expedite</option>
                    </select>
                  </td>
                  <td className="mono">
                    {onOpenProject
                      ? <a href="#projects" title="Open this project"
                          onClick={(e) => { e.preventDefault(); onOpenProject(p.projectId); }}>{p.projectId}</a>
                      : p.projectId}
                  </td>
                  <td>{project?.description?.slice(0, 26) ?? "—"}</td>
                  <td className="mono">{p.phaseCode}</td>
                  <td className="mono">{p.discipline?.replace("01-DFTG", "") ?? "—"}</td>
                  <td className="r mono">{p.estHours}</td>
                  <td className="r mono">
                    {p.remainingHours}
                    {p.overEstimate && (
                      <span className="chip exp" style={{ marginLeft: 6 }}
                        title={`${p.actHours} h logged against ${p.estHours} h estimated, and the step is not complete. Complete it, or give it a new estimate.`}>
                        over estimate
                      </span>
                    )}
                  </td>
                  <td className={`mono${late ? " late" : ""}`}>{p.reqDueDate ?? "—"}</td>
                  <td><span className="chip low">{p.epicorEngStatus ?? p.workflowState}</span></td>
                  <td>
                    <select
                      value={a.resourceId ?? ""}
                      onChange={(e) => assign(a.assignmentId, e.target.value || null)}
                    >
                      <option value="">— unassigned —</option>
                      {board.resources
                        // Inactive is the sole lever for keeping someone out of
                        // the solver, but nothing stopped a human from picking
                        // them here anyway. The current assignee stays listed
                        // even if they went inactive after being assigned, so
                        // an existing assignment does not silently vanish from
                        // its own dropdown.
                        .filter((r) => r.active || r.resourceId === a.resourceId)
                        .map((r) => (
                          <option key={r.resourceId} value={r.resourceId}>{r.name}</option>
                        ))}
                    </select>
                    {rowErr[a.assignmentId] && (
                      <div className="err" style={{ fontSize: 11, marginTop: 4 }}>
                        {rowErr[a.assignmentId]}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="hint">
        ⠿ drag a row onto another to reorder — every downstream date for that person recalculates.
        Changing the assignee moves the phase to the end of that person's queue.
      </p>
    </div>
  );
}

export function Capacity({ board }: { board: Board }) {
  const names = useMemo(() => new Map(board.resources.map((r) => [r.resourceId, r.name])), [board.resources]);
  const weeks = useMemo(
    () => [...new Set(board.utilization.map((u) => u.weekStart))].sort().slice(0, 8),
    [board.utilization],
  );
  const grid = useMemo(() => {
    const m = new Map<string, Map<string, number>>();
    for (const u of board.utilization) {
      const row = m.get(u.resourceId) ?? new Map<string, number>();
      row.set(u.weekStart, u.pct);
      m.set(u.resourceId, row);
    }
    return m;
  }, [board.utilization]);

  const band = (p: number) => (p > 1 ? "h4" : p > 0.9 ? "h3" : p > 0.6 ? "h2" : p > 0.3 ? "h1" : "h0");
  const rows = [...grid.entries()]
    .map(([id, row]) => ({ id, row, peak: Math.max(...weeks.map((w) => row.get(w) ?? 0)) }))
    .sort((a, b) => b.peak - a.peak);

  return (
    <div className="scroll tall">
      <div className="heat" style={{ ["--weeks" as string]: weeks.length }}>
        <div className="heat-row hdr">
          <div className="heat-name">Resource</div>
          {weeks.map((w) => (
            <div className="heat-cell" key={w}>
              {new Date(`${w}T00:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })}
            </div>
          ))}
        </div>
        {rows.map(({ id, row }) => (
          <div className="heat-row" key={id}>
            <div className="heat-name">{names.get(id) ?? id}</div>
            {weeks.map((w) => {
              const pct = row.get(w) ?? 0;
              return (
                <div className={`heat-cell ${band(pct)}`} key={w}>
                  {Math.round(pct * 100)}%
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="legend">
        <span>How full the derived schedule is, against the 80% rule — never exceeds 100%</span>
        <span>Overload is on the Schedule board as 4-week pressure, not here</span>
        <span>A drop to 0% is where scheduled work runs out, not free capacity</span>
      </div>
    </div>
  );
}
